import jsPDF from 'jspdf'
import { Invoice } from '@/types'
import { COMPANY } from '@/config/company'
import logo from '@/assets/logo.png'

/**
 * Branded invoice template.
 *
 * Renders an Invoice to a print-ready A4 PDF with the company letterhead,
 * bill-to block, line items, totals, and payment terms. Used by both the admin
 * invoice screen and the customer's copy so the two are always identical.
 */

const PAGE = { width: 595.28, height: 841.89 }
const MARGIN = 48
const CONTENT_WIDTH = PAGE.width - MARGIN * 2

// Matches the app's blue/purple scheme.
const BLUE: [number, number, number] = [37, 99, 235]
const PURPLE: [number, number, number] = [124, 58, 237]
const INK: [number, number, number] = [17, 24, 39]
const MUTED: [number, number, number] = [107, 114, 128]
const HAIRLINE: [number, number, number] = [226, 232, 240]

/** Firestore hands back Timestamps, plain Dates, or strings depending on path. */
function toDate(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    try {
      const d = (value as { toDate: () => Date }).toDate()
      return isNaN(d.getTime()) ? null : d
    } catch {
      return null
    }
  }
  const d = new Date(value as string | number)
  return isNaN(d.getTime()) ? null : d
}

function formatDate(value: unknown): string {
  const d = toDate(value)
  return d
    ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—'
}

function formatMoney(amount: number | undefined, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount || 0)
}

/** jsPDF needs pixels on hand, not a URL, so resolve the logo first. */
function loadLogo(): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null) // a missing logo shouldn't block the invoice
    img.src = logo
  })
}

export async function buildInvoicePdf(invoice: Invoice): Promise<jsPDF> {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const logoImg = await loadLogo()

  /* ---------------------------------------------------------------- *
   * Letterhead
   * ---------------------------------------------------------------- */
  doc.setFillColor(...BLUE)
  doc.rect(0, 0, PAGE.width, 4, 'F')
  doc.setFillColor(...PURPLE)
  doc.rect(PAGE.width / 2, 0, PAGE.width / 2, 4, 'F')

  let y = MARGIN + 12

  if (logoImg) {
    doc.addImage(logoImg, 'PNG', MARGIN, y - 6, 42, 42)
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(15)
  doc.setTextColor(...INK)
  doc.text(COMPANY.name, MARGIN + (logoImg ? 54 : 0), y + 10)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(...MUTED)
  const companyLines = [...COMPANY.addressLines, COMPANY.email, COMPANY.phone]
  companyLines.forEach((line, i) => {
    doc.text(line, MARGIN + (logoImg ? 54 : 0), y + 24 + i * 11)
  })

  // INVOICE title, right aligned
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(26)
  doc.setTextColor(...BLUE)
  doc.text('INVOICE', PAGE.width - MARGIN, y + 12, { align: 'right' })

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(...INK)
  doc.text(`#${invoice.invoiceNumber || '—'}`, PAGE.width - MARGIN, y + 30, { align: 'right' })

  // Status pill
  const status = (invoice.status || 'draft').toUpperCase()
  const isPaid = invoice.status === 'paid'
  const isOverdue = invoice.status === 'overdue'
  const pillColor: [number, number, number] = isPaid
    ? [22, 163, 74]
    : isOverdue
    ? [220, 38, 38]
    : [217, 119, 6]

  doc.setFontSize(8)
  doc.setFont('helvetica', 'bold')
  const pillWidth = doc.getTextWidth(status) + 16
  doc.setFillColor(...pillColor)
  doc.roundedRect(PAGE.width - MARGIN - pillWidth, y + 38, pillWidth, 16, 8, 8, 'F')
  doc.setTextColor(255, 255, 255)
  doc.text(status, PAGE.width - MARGIN - pillWidth / 2, y + 49, { align: 'center' })

  y += 84

  doc.setDrawColor(...HAIRLINE)
  doc.setLineWidth(1)
  doc.line(MARGIN, y, PAGE.width - MARGIN, y)
  y += 24

  /* ---------------------------------------------------------------- *
   * Bill to  /  invoice meta
   * ---------------------------------------------------------------- */
  const metaX = PAGE.width - MARGIN - 180

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(...MUTED)
  doc.text('BILL TO', MARGIN, y)
  doc.text('DETAILS', metaX, y)

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.setTextColor(...INK)
  doc.text(invoice.customer?.name || 'Customer', MARGIN, y + 16)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(...MUTED)

  const addr = invoice.customer?.address
  const billLines = [
    invoice.customer?.email,
    invoice.customer?.phone,
    addr?.street,
    [addr?.city, addr?.state, addr?.zipCode].filter(Boolean).join(', '),
    addr?.country,
  ].filter((l): l is string => Boolean(l && l.trim()))

  billLines.forEach((line, i) => doc.text(line, MARGIN, y + 30 + i * 11))

  const metaRows: [string, string][] = [
    ['Issued', formatDate(invoice.createdAt)],
    ['Due', formatDate(invoice.dueDate)],
  ]
  if (invoice.shipment?.trackingNumber) {
    metaRows.push(['Tracking', invoice.shipment.trackingNumber])
  }
  if (isPaid && invoice.paidDate) {
    metaRows.push(['Paid', formatDate(invoice.paidDate)])
  }
  if (invoice.paymentData?.paymentMethod) {
    metaRows.push([
      'Method',
      invoice.paymentData.paymentMethod === 'cash' ? 'Cash' : 'Bank transfer',
    ])
  }

  metaRows.forEach(([label, value], i) => {
    doc.setTextColor(...MUTED)
    doc.text(label, metaX, y + 16 + i * 13)
    doc.setTextColor(...INK)
    doc.text(value, PAGE.width - MARGIN, y + 16 + i * 13, { align: 'right' })
  })

  y += Math.max(30 + billLines.length * 11, 16 + metaRows.length * 13) + 28

  /* ---------------------------------------------------------------- *
   * Line items
   * ---------------------------------------------------------------- */
  const col = {
    desc: MARGIN,
    qty: MARGIN + CONTENT_WIDTH * 0.56,
    unit: MARGIN + CONTENT_WIDTH * 0.72,
    total: PAGE.width - MARGIN,
  }

  doc.setFillColor(248, 250, 252)
  doc.rect(MARGIN, y - 12, CONTENT_WIDTH, 24, 'F')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(...MUTED)
  doc.text('DESCRIPTION', col.desc + 8, y + 3)
  doc.text('QTY', col.qty, y + 3, { align: 'right' })
  doc.text('UNIT PRICE', col.unit, y + 3, { align: 'right' })
  doc.text('AMOUNT', col.total - 8, y + 3, { align: 'right' })

  y += 26

  const items = invoice.items?.length ? invoice.items : []

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)

  if (!items.length) {
    doc.setTextColor(...MUTED)
    doc.text('No line items on this invoice.', col.desc + 8, y + 4)
    y += 24
  }

  items.forEach((item) => {
    // Start a new page before we run off the bottom.
    if (y > PAGE.height - 200) {
      doc.addPage()
      y = MARGIN
    }

    doc.setTextColor(...INK)
    const descLines = doc.splitTextToSize(
      item.description || 'Item',
      CONTENT_WIDTH * 0.52
    ) as string[]
    descLines.forEach((line, i) => doc.text(line, col.desc + 8, y + 4 + i * 12))

    doc.text(String(item.quantity ?? 1), col.qty, y + 4, { align: 'right' })
    doc.text(formatMoney(item.unitPrice, invoice.currency), col.unit, y + 4, { align: 'right' })
    doc.setFont('helvetica', 'bold')
    doc.text(formatMoney(item.total, invoice.currency), col.total - 8, y + 4, { align: 'right' })
    doc.setFont('helvetica', 'normal')

    y += Math.max(descLines.length * 12, 12) + 12

    doc.setDrawColor(...HAIRLINE)
    doc.line(MARGIN, y - 6, PAGE.width - MARGIN, y - 6)
  })

  /* ---------------------------------------------------------------- *
   * Totals
   * ---------------------------------------------------------------- */
  y += 10
  const totalsX = PAGE.width - MARGIN - 200

  const totalRows: [string, string, boolean][] = [
    ['Subtotal', formatMoney(invoice.subtotal, invoice.currency), false],
  ]
  if (invoice.freight) {
    totalRows.push(['Freight', formatMoney(invoice.freight, invoice.currency), false])
  }
  totalRows.push(['Tax', formatMoney(invoice.tax, invoice.currency), false])
  totalRows.push(['Total due', formatMoney(invoice.total, invoice.currency), true])

  totalRows.forEach(([label, value, emphasis]) => {
    if (emphasis) {
      y += 6
      doc.setDrawColor(...INK)
      doc.setLineWidth(1)
      doc.line(totalsX, y - 8, PAGE.width - MARGIN, y - 8)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(13)
      doc.setTextColor(...INK)
    } else {
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(10)
      doc.setTextColor(...MUTED)
    }
    doc.text(label, totalsX, y + 6)
    if (emphasis) doc.setTextColor(...BLUE)
    doc.text(value, PAGE.width - MARGIN, y + 6, { align: 'right' })
    y += emphasis ? 26 : 18
  })

  /* ---------------------------------------------------------------- *
   * Notes + payment terms
   * ---------------------------------------------------------------- */
  y += 12

  if (invoice.notes) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(...MUTED)
    doc.text('NOTES', MARGIN, y)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(...INK)
    const noteLines = doc.splitTextToSize(invoice.notes, CONTENT_WIDTH) as string[]
    noteLines.forEach((line, i) => doc.text(line, MARGIN, y + 14 + i * 11))
    y += 14 + noteLines.length * 11 + 14
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(...MUTED)
  doc.text('PAYMENT', MARGIN, y)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  doc.setTextColor(...INK)
  doc.text(
    isPaid
      ? 'Paid in full — thank you.'
      : `Payable by the due date above. Cash or bank transfer accepted. Questions: ${COMPANY.email}`,
    MARGIN,
    y + 14,
    { maxWidth: CONTENT_WIDTH }
  )

  /* ---------------------------------------------------------------- *
   * Footer on every page
   * ---------------------------------------------------------------- */
  const pageCount = doc.getNumberOfPages()
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p)
    doc.setDrawColor(...HAIRLINE)
    doc.setLineWidth(1)
    doc.line(MARGIN, PAGE.height - 56, PAGE.width - MARGIN, PAGE.height - 56)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(...MUTED)
    doc.text(`${COMPANY.name} · ${COMPANY.email}`, MARGIN, PAGE.height - 40)
    doc.text(`Page ${p} of ${pageCount}`, PAGE.width - MARGIN, PAGE.height - 40, {
      align: 'right',
    })
  }

  return doc
}

/** Filename that sorts and reads well in a downloads folder. */
function fileName(invoice: Invoice): string {
  const who = (invoice.customer?.name || 'customer').replace(/[^a-z0-9]+/gi, '-').toLowerCase()
  return `invoice-${invoice.invoiceNumber || invoice.id}-${who}.pdf`
}

export async function downloadInvoicePdf(invoice: Invoice): Promise<void> {
  const doc = await buildInvoicePdf(invoice)
  doc.save(fileName(invoice))
}

/** Opens the browser's print dialog on the rendered PDF. */
export async function printInvoicePdf(invoice: Invoice): Promise<void> {
  const doc = await buildInvoicePdf(invoice)
  doc.autoPrint()
  const url = doc.output('bloburl')
  window.open(url as unknown as string, '_blank')
}
